"use client";

import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import { useAppSelector } from "@/store/hooks";
import { PageType } from "@/types/coupon/discount";
import FormFieldWrapper from "./formFieldWrapper";
import InputWithDropdown from "./inputWithDropdown";
import { InputPrimary } from "../common/InputPrimary";
import { useState } from "react";
import { toast } from "sonner";

const DiscountValueSection = ({ currentPage }: { currentPage: PageType }) => {
  const [error, setError] = useState<string | null>(null);
  const handleUpdate = useDiscountUpdate();
  const {
    discount_type,
    discount_value_fixed,
    discount_value_percentage,
    discount_value_max,
  } = useAppSelector((state) => state.discount[currentPage]);

  const handleInputChange = ({
    field,
    value,
  }: {
    field: string;
    value: string;
  }) => {
    // If user types '-', erase it and show error
    if (value.includes("-")) {
      setError("Negative values are not allowed.");
      handleUpdate(currentPage, { [field]: "" });
      return;
    }
    if (parseFloat(value) < 0) {
      setError("Negative values are not allowed.");
      handleUpdate(currentPage, { [field]: "" });
      return;
    }
    if (field === "discount_value_percentage") {
      if (parseFloat(value) > 100) {
        toast.error("Percentage cannot exceed 100%.");
        handleUpdate(currentPage, { [field]: "100" });
        return;
      }
    }
    setError(null);
    handleUpdate(currentPage, { [field]: value });
  };

  const isDiscountValueFixed = discount_type === "fixed";

  return (
    <FormFieldWrapper title="Discount Value">
      <div className="space-y-[10px]">
        <div>
          <label className="block text-[13px] font-normal mb-1">
            Discount Amount
          </label>
          <InputWithDropdown
            value={
              isDiscountValueFixed
                ? discount_value_fixed
                : discount_value_percentage
            }
            onInputChange={(e) => {
              const val = e.target.value;
              if (val.includes("-") || parseFloat(val) < 0) {
                handleInputChange({
                  field: isDiscountValueFixed
                    ? "discount_value_fixed"
                    : "discount_value_percentage",
                  value: "",
                });
                return;
              }
              if (!isDiscountValueFixed && parseFloat(val) > 100) {
                toast.error("Percentage cannot exceed 100%.");
                handleInputChange({
                  field: "discount_value_percentage",
                  value: "100",
                });
                return;
              }
              handleInputChange({
                field: isDiscountValueFixed
                  ? "discount_value_fixed"
                  : "discount_value_percentage",
                value: val,
              });
            }}
            selectedDropdownValue={discount_type}
            onDropdownChange={(e) =>
              handleInputChange({ field: "discount_type", value: e })
            }
            dropdownOptions={[
              {
                label: "Fixed Amount",
                value: "fixed",
              },
              {
                label: "Percentage",
                value: "percentage",
              },
            ]}
            inputProps={{
              type: "number",
              placeholder: "0.00",
              min: 0,
              max: !isDiscountValueFixed ? 100 : undefined,
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  e.key === "-" ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+"
                ) {
                  e.preventDefault();
                }
              },
            }}
          />
        </div>
        <div>
          <label className="block text-[13px] font-normal mb-1">
            Maximum discount value
          </label>
          <InputPrimary
            onChange={(e) => {
              const val = e.target.value;
              if (val.includes("-") || parseFloat(val) < 0) {
                handleInputChange({ field: "discount_value_max", value: "" });
                return;
              }
              handleInputChange({ field: "discount_value_max", value: val });
            }}
            type="number"
            placeholder="0"
            min={0}
            className="bg-transparent border rounded-xl h-[50px] px-4 py-2 placeholder-gray-500"
            value={discount_value_max}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
      </div>
    </FormFieldWrapper>
  );
};

export default DiscountValueSection;
